import {
  uploadFileToS3,
  useCompleteDataVersionUpload,
  useCreateDataVersion,
} from '@/queries/dataset-data-versions';
import { useCreateDatasetSchemaVersion } from '@/queries/dataset-schema-versions';
import { useUpdateDataset } from '@/queries/datasets';
import {
  formatBytes,
  getErrorMessage,
} from '@/utils';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloudUploadOutlined from '@ant-design/icons/CloudUploadOutlined';
import FileExcelOutlined from '@ant-design/icons/FileExcelOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Modal from 'antd/es/modal';
import Progress from 'antd/es/progress';
import Select from 'antd/es/select';
import Space from 'antd/es/space';
import Table from 'antd/es/table';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import Upload from 'antd/es/upload';
import type { RcFile, UploadFile } from 'antd/es/upload';
import React, { useState } from 'react';
import { styled, useTheme } from 'styled-components';

const { Text, Title } = Typography;
const { Dragger } = Upload;

const UploadContainer = styled.div`
  ${({ theme }) => `
  margin: ${theme.marginLG}px 0;
`}
`;

const FileInfoContainer = styled.div`
  ${({ theme }) => `
  background: ${theme.colorBgContainer};
  border: ${theme.lineWidth}px solid ${theme.colorBorder};
  border-radius: ${theme.borderRadiusLG}px;
  padding: ${theme.padding}px;
  margin: ${theme.margin}px 0;
`}
`;

const FileInfoRow = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.marginXS}px;

  &:last-child {
    margin-bottom: 0;
  }
`}
`;

const StatusContainer = styled.div`
  ${({ theme }) => `
  text-align: center;
  padding: ${theme.paddingXL}px;
`}
`;

const PreviewContainer = styled.div`
  ${({ theme }) => `
  margin-top: ${theme.marginMD}px;
  border: ${theme.lineWidth}px solid ${theme.colorBorder};
  border-radius: ${theme.borderRadius}px;
  overflow: hidden;
`}
`;

const PreviewHeader = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingSM}px ${theme.padding}px;
  background: ${theme.colorFillQuaternary};
  border-bottom: ${theme.lineWidth}px solid ${theme.colorBorder};
  font-weight: ${theme.fontWeightStrong};
`}
`;

const LoadingContainer = styled.div`
  ${({ theme }) => `
  text-align: center;
  padding: ${theme.paddingMD}px;
`}
`;

const ActionContainer = styled.div`
  ${({ theme }) => `
  margin-top: ${theme.marginLG}px;
  text-align: center;
`}
`;

const StatusTitle = styled(Title)`
  ${({ theme }) => `
  margin-top: ${theme.margin}px;
`}
`;

const ProgressContainer = styled.div`
  ${({ theme }) => `
  max-width: ${theme.controlHeightLG * 10}px;
  margin: 0 auto;
`}
`;

const StatusText = styled(Text)`
  ${({ theme }) => `
  margin-top: ${theme.marginXS}px;
`}
`;

interface UploadDataModalProps {
  open: boolean;
  onCancel: () => void;
  datasetId: string;
  onSuccess?: () => void;
}

type UploadStatus =
  | 'idle'
  | 'preview'
  | 'uploading'
  | 'processing'
  | 'success'
  | 'error';

interface DataPreview {
  headers: string[];
  rows: string[][];
  detectedTypes: Record<string, string>;
}

export const UploadDataModal: React.FC<UploadDataModalProps> = ({
  open,
  onCancel,
  datasetId,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [fileType, setFileType] = useState<'csv' | 'xlsx'>('csv');
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dataPreview, setDataPreview] = useState<DataPreview | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const theme = useTheme();
  const createDataVersionMutation = useCreateDataVersion();
  const completeUploadMutation = useCompleteDataVersionUpload();
  const createSchemaVersionMutation = useCreateDatasetSchemaVersion();
  const updateDatasetMutation = useUpdateDataset();

  // Transform detected types into schema column format
  const createSchemaFromPreview = (
    preview: DataPreview,
    delimiter?: string,
  ) => {
    const columns = preview.headers
      .filter((header) => header && header.trim().length > 0)
      .map((header, index) => ({
        name: header.trim(),
        type: preview.detectedTypes[header] || 'string',
        nullable: true,
        pos: index + 1,
        source: header.trim(),
      }));

    return {
      datasetId,
      columns,
      status: 'current' as const,
      ...(delimiter && { parsingOptions: { delimiter } }),
    };
  };

  // Auto-detect CSV delimiter
  const detectDelimiter = (text: string): string => {
    const sample = text.split('\n').slice(0, 5).join('\n'); // Check first 5 lines
    const delimiters = [',', ';', '\t', '|'];
    const counts = delimiters.map((delim) => ({
      delimiter: delim,
      count: (sample.match(new RegExp(`\\${delim}`, 'g')) || []).length,
    }));

    // Return the delimiter with the highest count
    const best = counts.reduce((prev, curr) =>
      curr.count > prev.count ? curr : prev,
    );
    return best.count > 0 ? best.delimiter : ',';
  };

  // Proper CSV parser that handles quoted fields with newlines and commas
  const parseCSV = (text: string, delimiter?: string): string[][] => {
    const actualDelimiter = delimiter || detectDelimiter(text);
    const result: string[][] = [];
    let currentRow: string[] = [];
    let currentField = '';
    let inQuotes = false;
    let i = 0;

    while (i < text.length) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote inside quoted field
          currentField += '"';
          i += 2;
          continue;
        } else {
          // Start or end of quoted field
          inQuotes = !inQuotes;
        }
      } else if (char === actualDelimiter && !inQuotes) {
        // Field separator
        currentRow.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        // Row separator
        if (currentField || currentRow.length > 0) {
          currentRow.push(currentField.trim());
          if (currentRow.some((field) => field.length > 0)) {
            result.push(currentRow);
          }
          currentRow = [];
          currentField = '';
        }
        // Skip \r\n sequence
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
      } else {
        currentField += char;
      }
      i++;
    }

    // Handle last field/row
    if (currentField || currentRow.length > 0) {
      currentRow.push(currentField.trim());
      if (currentRow.some((field) => field.length > 0)) {
        result.push(currentRow);
      }
    }

    return result;
  };

  // Helper function to generate data preview
  const generateDataPreview = async (
    file: File,
    fileType: 'csv' | 'xlsx',
  ): Promise<DataPreview> => {
    if (fileType === 'csv') {
      const text = await file.text();
      const parsedRows = parseCSV(text);

      if (parsedRows.length === 0) {
        throw new Error('No data found in CSV file');
      }

      if (parsedRows.length === 1) {
        throw new Error('Only header row found, no data rows detected');
      }

      if (parsedRows[0].length === 1) {
        throw new Error(
          'Only one column detected. Check if the delimiter is correct.',
        );
      }

      const headers = parsedRows[0] || [];
      const rows = parsedRows.slice(1, 6); // Show first 5 data rows

      // Enhanced type detection
      const detectedTypes: Record<string, string> = {};
      headers.forEach((header, index) => {
        const sampleValues = rows
          .map((row) => row[index])
          .filter((val) => val && val.trim() !== '')
          .slice(0, 10); // Check first 10 non-empty values

        if (sampleValues.length === 0) {
          detectedTypes[header] = 'string';
          return;
        }

        // Check for boolean values
        const booleanValues = ['true', 'false', 'yes', 'no', '1', '0'];
        const isBooleanLike = sampleValues.every((val) =>
          booleanValues.includes(val.toLowerCase().trim()),
        );

        if (isBooleanLike) {
          detectedTypes[header] = 'boolean';
          return;
        }

        // Check for numeric values (including decimals)
        const isNumeric = sampleValues.every((val) => {
          const num = parseFloat(val.replace(/[,$%]/g, ''));
          return !isNaN(num) && isFinite(num);
        });

        if (isNumeric) {
          // Distinguish between integer and float
          const hasDecimals = sampleValues.some((val) => {
            const cleaned = val.replace(/[,$%]/g, '');
            return cleaned.includes('.') && !cleaned.endsWith('.0');
          });
          detectedTypes[header] = hasDecimals ? 'float' : 'integer';
          return;
        }

        // Check for date values
        const isDate = sampleValues.some((val) => {
          const date = new Date(val);
          return !isNaN(date.getTime()) && val.length > 4; // Avoid detecting years as dates
        });

        if (isDate) {
          detectedTypes[header] = 'datetime';
          return;
        }

        // Default to string
        detectedTypes[header] = 'string';
      });

      return {
        headers,
        rows,
        detectedTypes,
      };
    }

    // For Excel files, provide basic info
    return {
      headers: ['Excel file detected'],
      rows: [['Full preview available after upload']],
      detectedTypes: {},
    };
  };

  const handleFileChange = async (info: { fileList: UploadFile[] }) => {
    // Keep only the latest file
    const latestFile = info.fileList.slice(-1);
    setFileList(latestFile);

    // Auto-detect file type from extension
    if (latestFile.length > 0 && latestFile[0]?.name) {
      const fileName = latestFile[0].name.toLowerCase();
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        setFileType('xlsx');
      } else if (fileName.endsWith('.csv')) {
        setFileType('csv');
      }

      // Generate preview for CSV files
      if (latestFile[0]?.originFileObj) {
        setIsGeneratingPreview(true);
        try {
          const preview = await generateDataPreview(
            latestFile[0].originFileObj,
            fileType,
          );
          setDataPreview(preview);
          setUploadStatus('preview');

          // Show helpful message about detected structure
          if (fileType === 'csv') {
            const fileText = await latestFile[0].originFileObj.text();
            const detectedDelimiter = detectDelimiter(fileText);
            const delimiterName =
              detectedDelimiter === ','
                ? 'comma'
                : detectedDelimiter === ';'
                  ? 'semicolon'
                  : detectedDelimiter === '\t'
                    ? 'tab'
                    : 'pipe';
            void message.success(
              `Detected ${preview.headers.length} columns with ${delimiterName} delimiter`,
              3,
            );
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          void message.warning(
            `Could not analyze file structure: ${errorMessage}. You can still upload manually.`,
            5,
          );
          setUploadStatus('idle');
        } finally {
          setIsGeneratingPreview(false);
        }
      }
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isValidType =
      file.type === 'text/csv' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls');

    if (!isValidType) {
      void message.error('You can only upload CSV or Excel files!');
      return false;
    }

    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      void message.error('File must be smaller than 100MB!');
      return false;
    }

    return false; // Prevent automatic upload
  };

  const handleSmartUpload = async () => {
    if (fileList.length === 0 || !fileList[0]?.originFileObj) {
      void message.error('Please select a file to upload');
      return;
    }

    if (!dataPreview) {
      void message.error('Please wait for data preview to generate');
      return;
    }

    const file = fileList[0].originFileObj as RcFile;
    await performSmartUpload(file, dataPreview);
  };

  const handleUpload = async () => {
    if (fileList.length === 0 || !fileList[0]?.originFileObj) {
      void message.error('Please select a file to upload');
      return;
    }

    const file = fileList[0].originFileObj as RcFile;
    await performUpload(file);
  };

  const performSmartUpload = async (file: RcFile, preview: DataPreview) => {
    try {
      setUploadStatus('uploading');
      setUploadProgress(0);
      setErrorMessage(null);

      // Validate required data
      if (!datasetId) {
        throw new Error('Dataset ID is required');
      }

      if (!preview.headers || preview.headers.length === 0) {
        throw new Error('No columns detected in preview');
      }

      // Step 1 - Create schema version first
      const fileText = await file.text();
      const detectedDelimiter = detectDelimiter(fileText);
      const schemaData = createSchemaFromPreview(preview, detectedDelimiter);

      await createSchemaVersionMutation.mutateAsync(schemaData);

      // Step 2 - Create data version and get presigned URL
      const createResponse = await createDataVersionMutation.mutateAsync({
        datasetId,
        fileName: file.name,
        fileType,
        status: 'current',
      });

      // Step 3 - Upload file to S3 using presigned URL
      await uploadFileToS3(createResponse.uploadUrl, file, (progress) => {
        setUploadProgress(progress);
      });

      setUploadStatus('processing');

      // Step 4 - Mark upload as complete
      await completeUploadMutation.mutateAsync({
        dataVersionId: createResponse.dataVersion.datasetDataVersionId,
        data: {},
      });

      // Step 6 - Auto-trigger processing for Smart Upload
      await updateDatasetMutation.mutateAsync({
        datasetId,
        data: { shouldProcess: true },
      });

      setUploadStatus('success');
      void message.success(
        'Smart upload complete! Your data is being processed and will be ready for queries shortly.',
      );

      // Clean up and close
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 1500);
    } catch (error) {
      setUploadStatus('error');
      const errorMsg = getErrorMessage(
        error,
        'Failed to complete smart upload',
      );
      setErrorMessage(errorMsg);
      void message.error(errorMsg);
    }
  };

  const performUpload = async (file: RcFile) => {
    try {
      setUploadStatus('uploading');
      setUploadProgress(0);
      setErrorMessage(null);

      // Step 1 - Create data version and get presigned URL
      const createResponse = await createDataVersionMutation.mutateAsync({
        datasetId,
        fileName: file.name,
        fileType,
        status: 'current',
      });

      // Step 2 - Upload file to S3 using presigned URL
      await uploadFileToS3(createResponse.uploadUrl, file, (progress) => {
        setUploadProgress(progress);
      });

      setUploadStatus('processing');

      // Step 3 - Mark upload as complete
      await completeUploadMutation.mutateAsync({
        dataVersionId: createResponse.dataVersion.datasetDataVersionId,
        data: {},
      });

      setUploadStatus('success');
      void message.success('Data uploaded successfully!');

      // Clean up and close
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 1500);
    } catch (error) {
      setUploadStatus('error');
      const errorMsg = getErrorMessage(error, 'Failed to upload data');
      setErrorMessage(errorMsg);
      void message.error(errorMsg);
    }
  };

  const handleClose = () => {
    setFileList([]);
    setFileType('csv');
    setUploadStatus('idle');
    setUploadProgress(0);
    setErrorMessage(null);
    setDataPreview(null);
    setIsGeneratingPreview(false);
    onCancel();
  };

  const renderPreviewTable = () => {
    if (!dataPreview) return null;

    const columns = dataPreview.headers.map((header) => ({
      title: (
        <Space>
          <Text strong>{header}</Text>
          <Tag
            color={
              dataPreview.detectedTypes[header] === 'integer'
                ? 'blue'
                : dataPreview.detectedTypes[header] === 'float'
                  ? 'cyan'
                  : dataPreview.detectedTypes[header] === 'boolean'
                    ? 'purple'
                    : dataPreview.detectedTypes[header] === 'datetime'
                      ? 'orange'
                      : 'green'
            }
          >
            {dataPreview.detectedTypes[header] || 'string'}
          </Tag>
        </Space>
      ),
      dataIndex: header,
      key: header,
      ellipsis: true,
    }));

    const dataSource = dataPreview.rows.map((row, index) => {
      const rowData: Record<string, string> = { key: index.toString() };
      dataPreview.headers.forEach((header, colIndex) => {
        rowData[header] = row[colIndex] || '';
      });
      return rowData;
    });

    return (
      <PreviewContainer>
        <PreviewHeader>
          <Space>
            <Text strong>Data Preview</Text>
            <Text type="secondary">({dataPreview.rows.length} rows detected)</Text>
          </Space>
        </PreviewHeader>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </PreviewContainer>
    );
  };

  const renderContent = () => {
    switch (uploadStatus) {
      case 'uploading':
        return (
          <StatusContainer>
            <LoadingOutlined
              style={{
                fontSize: `${theme.fontSizeHeading1 + 10}px`,
                color: theme.colorPrimary,
              }}
            />
            <StatusTitle level={4}>Uploading your data...</StatusTitle>
            <ProgressContainer>
              <Progress percent={uploadProgress} />
            </ProgressContainer>
            <StatusText type="secondary">
              Please wait while we securely upload your file
            </StatusText>
          </StatusContainer>
        );

      case 'processing':
        return (
          <StatusContainer>
            <LoadingOutlined
              style={{
                fontSize: `${theme.fontSizeHeading1 + 10}px`,
                color: theme.colorPrimary,
              }}
              spin
            />
            <StatusTitle level={4}>Preparing your data...</StatusTitle>
            <StatusText type="secondary">
              Processing file and extracting metadata
            </StatusText>
          </StatusContainer>
        );

      case 'success':
        return (
          <StatusContainer>
            <CheckCircleOutlined
              style={{
                fontSize: `${theme.fontSizeHeading1 + 10}px`,
                color: theme.colorSuccess,
              }}
            />
            <StatusTitle level={4}>Upload successful!</StatusTitle>
            <StatusText type="secondary">
              Your data has been uploaded and is ready to use
            </StatusText>
          </StatusContainer>
        );

      case 'error':
        return (
          <StatusContainer>
            <StatusTitle level={4} style={{ color: theme.colorError }}>
              Upload failed
            </StatusTitle>
            <Text type="danger">{errorMessage}</Text>
            <Button
              type="primary"
              onClick={() => setUploadStatus('idle')}
              style={{ marginTop: `${theme.margin}px` }}
            >
              Try Again
            </Button>
          </StatusContainer>
        );

      case 'preview':
        return (
          <>
            <FileInfoContainer>
              <FileInfoRow>
                <Space>
                  {fileType === 'xlsx' ? (
                    <FileExcelOutlined />
                  ) : (
                    <FileTextOutlined />
                  )}
                  <Text strong>{fileList[0]?.name}</Text>
                </Space>
                <Text type="secondary">
                  {formatBytes(fileList[0]?.size || 0)}
                </Text>
              </FileInfoRow>
              <FileInfoRow>
                <Text>File Type:</Text>
                <Select
                  value={fileType}
                  onChange={setFileType}
                  style={{ width: `${theme.controlHeightLG * 3}px` }}
                  size="small"
                  options={[
                    { label: 'CSV', value: 'csv' },
                    { label: 'Excel', value: 'xlsx' },
                  ]}
                />
              </FileInfoRow>
            </FileInfoContainer>

            {renderPreviewTable()}

            <ActionContainer>
              <Space>
                <Button onClick={() => setUploadStatus('idle')}>
                  Select Different File
                </Button>
                <Button type="primary" onClick={handleSmartUpload}>
                  Upload & Process
                </Button>
              </Space>
            </ActionContainer>
          </>
        );

      default:
        return (
          <>
            <UploadContainer>
              <Dragger
                fileList={fileList}
                onChange={handleFileChange}
                beforeUpload={beforeUpload}
                maxCount={1}
                accept=".csv,.xlsx,.xls"
              >
                <p className="ant-upload-drag-icon">
                  <CloudUploadOutlined
                    style={{ fontSize: `${theme.fontSizeHeading1 + 10}px` }}
                  />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for CSV and Excel files up to 100MB
                </p>
              </Dragger>
            </UploadContainer>

            {fileList.length > 0 && fileList[0] && (
              <FileInfoContainer>
                <FileInfoRow>
                  <Space>
                    {fileType === 'xlsx' ? (
                      <FileExcelOutlined />
                    ) : (
                      <FileTextOutlined />
                    )}
                    <Text strong>{fileList[0].name}</Text>
                  </Space>
                  <Text type="secondary">
                    {formatBytes(fileList[0].size || 0)}
                  </Text>
                </FileInfoRow>
                <FileInfoRow>
                  <Text>File Type:</Text>
                  <Select
                    value={fileType}
                    onChange={setFileType}
                    style={{ width: `${theme.controlHeightLG * 3}px` }}
                    size="small"
                    options={[
                      { label: 'CSV', value: 'csv' },
                      { label: 'Excel', value: 'xlsx' },
                    ]}
                  />
                </FileInfoRow>
              </FileInfoContainer>
            )}

            {isGeneratingPreview && (
              <LoadingContainer>
                <Space>
                  <LoadingOutlined spin />
                  <Text type="secondary">Analyzing your data...</Text>
                </Space>
              </LoadingContainer>
            )}
          </>
        );
    }
  };

  return (
    <Modal
      title="Upload Dataset Version"
      open={open}
      onCancel={
        uploadStatus === 'idle' ||
        uploadStatus === 'error' ||
        uploadStatus === 'preview'
          ? handleClose
          : undefined
      }
      footer={
        uploadStatus === 'idle'
          ? [
              <Button key="cancel" onClick={handleClose}>
                Cancel
              </Button>,
              <Button
                key="upload"
                type="primary"
                onClick={handleUpload}
                disabled={fileList.length === 0}
                icon={<UploadOutlined />}
              >
                Upload
              </Button>,
            ]
          : uploadStatus === 'preview'
            ? null // Footer is handled in the preview content
            : uploadStatus === 'error'
              ? [
                  <Button key="close" onClick={handleClose}>
                    Close
                  </Button>,
                ]
              : null
      }
      width={
        uploadStatus === 'preview'
          ? `${theme.controlHeightLG * 20}px`
          : `${theme.controlHeightLG * 15}px`
      }
      closable={
        uploadStatus === 'idle' ||
        uploadStatus === 'error' ||
        uploadStatus === 'preview'
      }
      maskClosable={false}
    >
      {renderContent()}
    </Modal>
  );
};

export default UploadDataModal;
